import * as React from "react";
import { Alert, Button, Card, notification, Tag } from "antd";
import { CardTitle } from "../../components/antd/card/CardTitle";
import { CaretRightOutlined } from "@ant-design/icons";
import { CardContent } from "../../components/antd/card/CardContent";
import { CardCollapsibleContent } from "../../components/antd/card/CardCollapsibleContent";
import { CardDescription } from "../../components/antd/card/CardDescription";
import { FormItemDescription } from "../../components/antd/form/FormItemDescription";
import { CodeEditor } from "./identityMatcher/codeEditor";
import { SyntaxHighlight } from "../../components/syntaxHighlight/SyntaxHighlight";
import { getToken, request } from "../../services/cpln";
import { jwtDecode } from "jwt-decode";
import { ExternalLink } from "../../newcomponents/table/components/ExternalLink";
import { formSet, formSetValue, useCpFormValue } from "../gvc/testNewForm/utils";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { CpSwitch } from "../gvc/testNewForm/CpSwitch";
import { CpLabel } from "../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../gvc/testNewForm/CpErrorWarning";
import { CpFormItem } from "../gvc/testNewForm/CpFormItem";
import { CpSelect } from "../gvc/testNewForm/CpSelect";
import { store } from "../../store";

interface Props {}
export const IdentityMatcher: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const isEnabled = useCpFormValue(`identityMatcher.isEnabled`);
  const language = useCpFormValue(`identityMatcher.language`);

  // test UI
  const isTestUICollapsed = useCpFormValue(`identityMatcher.isTestUICollapsed`);
  const target = useCpFormValue(`identityMatcher.target`);
  const previousTarget = useCpFormValue(`identityMatcher.previousTarget`);
  const targetError = useCpFormValue(`identityMatcher.targetError`);
  const evaluation = useCpFormValue(`identityMatcher.evaluation`);

  React.useEffect(() => {
    if (!isEnabled || isTestUICollapsed) {
      return;
    }
    useMyToken();
    notification.info({ message: "Using current token as target" });
  }, [isTestUICollapsed]);

  React.useEffect(() => {
    if (!target) {
      return;
    }
    if (target === previousTarget) {
      return;
    }
    formSetValue(id, `identityMatcher.previousTarget`, target);
    formSetValue(id, `identityMatcher.target`, "");

    let isJson = true;
    let _target = target;
    try {
      const obj = JSON.parse(target);
      _target = JSON.stringify(obj, null, 2);
    } catch (e) {
      isJson = false;
    }
    if (!isJson) {
      try {
        let decoded = jwtDecode(target);
        _target = JSON.stringify((decoded as any).firebase, null, 2);
      } catch (e) {
        console.error(e.message);
        formSetValue(id, `identityMatcher.targetError`, "Typed value must be a valid json.");
      }
    }
    formSetValue(id, `identityMatcher.target`, _target);
  }, [target]);

  const expression = useCpFormValue(`identityMatcher.expression`);
  const [expressionLocal, setExpressionLocal] = React.useState(expression);
  React.useEffect(() => {
    if (expression !== expressionLocal) {
      setExpressionLocal(expression);
    }
  }, [expression]);
  React.useEffect(() => {
    if (expression !== expressionLocal) {
      formSetValue(id, `identityMatcher.expression`, expressionLocal);
    }
  }, [expressionLocal]);

  function useMyToken() {
    formSetValue(id, `identityMatcher.targetError`, "");
    formSetValue(id, `identityMatcher.target`, "");
    setTimeout(() => {
      getToken().then((token) => {
        let _token = token;
        try {
          _token = jwtDecode(token);
          _token = JSON.stringify((_token as any).firebase, null, 2);
        } catch (e) {
          formSetValue(id, `identityMatcher.targetError`, "Failed to parse token");
        }
        formSetValue(id, `identityMatcher.target`, _token);
      });
    }, 100);
  }

  async function onTest() {
    let _target = target;
    try {
      _target = JSON.parse(target as string);
    } catch (e) {}
    const { data: evaluationRes } = await request({
      url: "/auth/checkMatchExpression",
      method: "post",
      body: {
        target: _target,
        language: language,
        expression: expression,
      },
    });
    delete evaluationRes.request;
    formSetValue(id, `identityMatcher.evaluation`, evaluationRes);
  }

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>Identity Matcher</CardTitle>
          <CardContent>
            <CardDescription>
              <div>
                Executes the expression against the users' claims to decide whether a user belongs to this group.
              </div>
              <div>This method is useful for managing the grouping of users logged-in with SAML providers.</div>
            </CardDescription>
            <CpFormItem>
              <div className="flex items-center gap-2">
                <CpLabel name={`identityMatcher.isEnabled`}>Configure Identity Matcher</CpLabel>
                <CpSwitch name={`identityMatcher.isEnabled`} />
              </div>
              <CpErrorWarning name={`identityMatcher.isEnabled`} />
            </CpFormItem>
            <CardCollapsibleContent collapsed={!isEnabled}>
              <CpFormItem>
                <CpLabel name={`identityMatcher.language`}>Language</CpLabel>
                <CpSelect
                  name={`identityMatcher.language`}
                  options={[
                    { label: "JavaScript", value: "javascript" },
                    { label: "JMESPath", value: "jmespath" },
                  ]}
                  style={{ width: 250 }}
                />
                <CpErrorWarning name={`identityMatcher.language`} />
              </CpFormItem>
              <div>Expression</div>
              <FormItemDescription>
                {language === "jmespath" ? (
                  <div className="flex items-center">
                    <span className="mr-1">Learn more about</span>
                    <ExternalLink label="jmespath" url={`https://jmespath.org/`} disableCopy />
                    <span className="ml-1">expression language.</span>
                  </div>
                ) : (
                  <div className="flex flex-col my-1">
                    <div>
                      Target object is accessed with <Tag className="font-mono">$</Tag> variable.
                    </div>
                    <div>Last line is returned as result.</div>
                    <div>
                      You can use{" "}
                      <Tag
                        // TODO can do this for all tags?
                        className="font-mono"
                      >
                        console.log
                      </Tag>{" "}
                      calls to debug.
                    </div>
                  </div>
                )}
              </FormItemDescription>
              <div className="mb-8">
                <CodeEditor
                  value={expressionLocal}
                  setValue={setExpressionLocal}
                  language={"javascript"}
                  options={{
                    basicSetup: {
                      lineNumbers: true,
                      autocompletion: language === "javascript",
                    },
                  }}
                  onChange={() => {
                    const name = "expression";
                    formSet(id, `fieldStates.${name}.modified`, true);

                    const form = store.getState().form.instances[id];
                    const onChangeCounter = form.meta.onChangeCounter;
                    formSet(id, `meta.onChangeCounter`, onChangeCounter + 1);
                  }}
                  onFocus={() => {
                    const name = "expression";
                    formSet(id, `fieldStates.${name}.focused`, true);
                    formSet(id, `fieldStates.${name}.visited`, true);
                  }}
                  onBlur={() => {
                    const name = "expression";
                    formSet(id, `fieldStates.${name}.focused`, false);
                    formSet(id, `fieldStates.${name}.touched`, true);

                    const form = store.getState().form.instances[id];
                    const onBlurCounter = form.meta.onBlurCounter;
                    formSet(id, `meta.onBlurCounter`, onBlurCounter + 1);
                  }}
                />
                <CpErrorWarning name={`identityMatcher.expression`} />
              </div>
              <CardTitle style={isTestUICollapsed ? { marginBottom: 0 } : undefined}>
                <div className={`flex items-baseline ${isTestUICollapsed ? "" : "mb-4"}`}>
                  <div
                    className={`flex items-baseline gap-2 color-link-hover cursor-pointer`}
                    onClick={() => {
                      formSetValue(id, `identityMatcher.isTestUICollapsed`, !isTestUICollapsed);
                    }}
                  >
                    <CaretRightOutlined rotate={isTestUICollapsed ? 0 : 90} className={`self-center`} />
                    <span>Test Expression</span>
                  </div>
                </div>
              </CardTitle>
              <CardCollapsibleContent collapsed={isTestUICollapsed}>
                <>
                  <div className="flex gap-4">
                    <div className="w-1/2">
                      <div className="mb-1 leading-tight">
                        Use the editor below which contains your current token by default to test your expression.
                      </div>
                      <div className="mb-2 leading-tight">
                        Usual javascript truth semantics hold ( null, false, 0, and "" evaluates to false). In addition
                        to these, empty objects {`{ }`} and empty arrays [ ] also are considered falsey.
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-1/2">
                      <CodeEditor
                        value={target || ""}
                        setValue={(v) => formSetValue(id, `identityMatcher.target`, v)}
                        language={"json"}
                        options={{ basicSetup: { lineNumbers: true, autocompletion: false } }}
                      />
                    </div>

                    <div className="w-1/2">
                      {targetError ? (
                        <Alert className="my-2" style={{ maxWidth: 450 }} message={targetError} type={"info"} />
                      ) : (
                        <Button
                          htmlType={`button`}
                          className="mt-2"
                          type="primary"
                          style={{ width: 450 }}
                          onClick={onTest}
                          disabled={(expression || "").length < 1 || (target || "").length < 1}
                        >
                          Test
                        </Button>
                      )}
                      {evaluation ? (
                        <>
                          <div className="mb-2 mt-4">Test Result</div>
                          <div className="mb-4">
                            <div className="mb-1">Match</div>
                            <pre className={evaluation.match ? "text-green" : "text-red"}>
                              {String(evaluation.match)}
                            </pre>
                          </div>
                          {evaluation?.error ? (
                            <div className="mb-4">
                              <div className="mb-1">Error</div>
                              <Alert
                                style={{ width: 450 }}
                                message={"Expression Error: " + evaluation.error}
                                type={"error"}
                              />
                            </div>
                          ) : (
                            <div className="mb-4">
                              <div className="mb-1">Value</div>
                              <SyntaxHighlight
                                code={JSON.stringify(evaluation.value, null, 2)}
                                language="json"
                                showLineNumbers
                              />
                            </div>
                          )}
                          {language === "javascript" ? (
                            <div className="mb-4">
                              <div className="mb-1">Logs</div>
                              <SyntaxHighlight
                                code={evaluation?.logs || ""}
                                language="json"
                                showLineNumbers
                              />
                            </div>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                  </div>
                </>
              </CardCollapsibleContent>
            </CardCollapsibleContent>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
